import { useState, useEffect, useRef } from 'react';
import styles from './importSummaryModal.module.scss';
import { IoMdClose } from 'react-icons/io';
import ButtonShared from '../Button';
import { MdFileDownload } from 'react-icons/md';
import { ImportSummaryModalProps } from '../../interfaces/components';
import { CircularProgress } from '@mui/material';

const ImportSummaryModal = ({
  isOpen,
  onClose,
  onComplete,
  vehicleType,
  totalCount,
  identifiedCount,
  incompleteCount,
  notImportedCount,
  fileName,
  archiveId,
  massiveCharge,
  validateUploadStatus
}: ImportSummaryModalProps) => {
  const dataStatusProcess = massiveCharge?.validationResult?.data?.process_publications;
  const [simulatedProgress, setSimulatedProgress] = useState(5);
  const progressInterval = useRef<NodeJS.Timeout | null>(null);

  const handleDownloadNotRecognized = () => {
    if (dataStatusProcess) {
      const downloadUrl = dataStatusProcess?.error_csv_file;

      const link = document.createElement('a');
      link.href = downloadUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    if (isOpen && archiveId) {
      validateUploadStatus(archiveId);

      setSimulatedProgress(5);
    }
  }, [isOpen, archiveId, validateUploadStatus]);

  useEffect(() => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }

    if (isOpen && !dataStatusProcess) {
      progressInterval.current = setInterval(() => {
        setSimulatedProgress(prevProgress => {
          const increment = Math.random() * 2 + 0.5;
          const newProgress = prevProgress + increment;

          return newProgress > 95 ? 95 : newProgress;
        });
      }, 800);
    }

    if (dataStatusProcess) {
      setSimulatedProgress(100);
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    }

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    };
  }, [isOpen, dataStatusProcess]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
          <button 
          className={`${styles.closeButton} ${(massiveCharge?.confirmStorage?.success !== true || massiveCharge?.confirmStorage === null) ? styles.disabledButton : ''}`} 
          onClick={onComplete}
          disabled={massiveCharge?.confirmStorage?.success !== true || massiveCharge?.confirmStorage === null}
        >
          <IoMdClose />
        </button>

        <div className={styles.modalHeader}>
          <h2>Resumen de tu importación</h2>
        </div>

        <div className={styles.modalContent}>
          {(massiveCharge?.uploadProgress !== undefined && massiveCharge.uploadProgress < 100) ? (
            <div className={styles.loaderContainer}>
              <p className={styles.validationStatus}>
                {massiveCharge?.uploadProgress >= 90 ? 'Finalizando procesamiento...' :
                  massiveCharge?.uploadProgress >= 50 ? 'Procesando archivos...' :
                    'Preparando archivos...'}
              </p>

              <div className={styles.progressContainer}>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressBarFill}
                    style={{ width: `${massiveCharge?.uploadProgress}%` }}
                  />
                </div>
                <p className={styles.progressText}>{Math.round(massiveCharge?.uploadProgress)}%</p>
              </div>
            </div>
          ) : massiveCharge.validationResult && dataStatusProcess ? (
            <>
              <p className={styles.summaryText}>
                Se añadirán <span className={styles.totalCount}>{dataStatusProcess?.processed_records}</span> {dataStatusProcess?.processed_records === 1 ? 'auto' : 'autos'} a tu stock de autos
                <span className={styles.vehicleType}>{vehicleType === 'new' ? ' nuevos' : ' usados'}</span>
              </p>

              <div className={styles.detailsList}>
                <ul>
                  <li>Autos identificados: <span className={styles.count}>{dataStatusProcess?.vehicle_data?.length}</span></li>
                  <li>Autos con datos incompletos: <span className={styles.count}>{dataStatusProcess?.processing_errors?.length}</span></li>
                </ul>
              </div>

              <hr className={styles.divider} />

              <p className={styles.notImportedText}>
                Del total que subiste, <span className={styles.totalCount}>{dataStatusProcess?.processing_errors?.length}</span> autos no se importaron por falta de datos. Tampoco se importaron sus imágenes
              </p>

              {fileName && dataStatusProcess?.error_csv_file && (
                <div className={styles.downloadLink}>
                  <a href="#" onClick={(e) => {
                    e.preventDefault();
                    handleDownloadNotRecognized();
                  }}>
                    <MdFileDownload className={styles.downloadIcon} />
                    <span>{fileName}</span>
                  </a>
                </div>
              )}
            </>
          ) : (
            <p className={styles.validationStatus}>Esperando validación...</p>
          )}
        </div>

        <div className={styles.infoContainer}>
          <p className={styles.infoText}>Las imágenes continúan siendo procesadas. Esto puede tardar varios minutos...</p>
        </div>

        <div className={styles.modalFooter}>
          <ButtonShared
            onPress={onComplete}
            title="Finalizar"
            primary
            className={styles.completeButton}
            disabled={massiveCharge?.confirmStorage?.success !== true || massiveCharge?.confirmStorage === null}
          />
        </div>
      </div>
    </div>
  );
};

export default ImportSummaryModal;
